import React, { useState, useEffect, useRef, useCallback } from 'react';
import apiService from '../services/api';
import './Sidebar.css';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import BusinessIcon from '@mui/icons-material/Business';
import BarChartIcon from '@mui/icons-material/BarChart';
import SettingsIcon from '@mui/icons-material/Settings';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import GroupIcon from '@mui/icons-material/Group';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LogoutIcon from '@mui/icons-material/Logout';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';

// Fetch icons via apiService
const Logo = apiService.getBlobAssetUrl('CSAT LOgo.svg');

const logout_url = window._env_?.REACT_APP_LOGOUT_URL;

const Sidebar = ({ onToggleSidebar = () => { }, isCollapsed }) => {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [hoveringSettings, setHoveringSettings] = useState(false);
  const [wasManuallyToggled, setWasManuallyToggled] = useState(false);
  const [imageURL, setImageURL] = useState(sessionStorage.getItem('image_url'));
  const location = useLocation();
  const userName = sessionStorage.getItem('user_name');
  // eslint-disable-next-line
  const userEmail = sessionStorage.getItem('user_emailId');
  const moduleAccess = JSON.parse(sessionStorage.getItem('module_access') || '{}');

  useEffect(() => {
    if (!wasManuallyToggled) {
      if (!isCollapsed && location.pathname.startsWith('/settings')) {
        setSettingsOpen(true);
      } else if (isCollapsed) {
        setSettingsOpen(false);
      }
    }
    setWasManuallyToggled(false);
  }, [isCollapsed, location.pathname]);

  useEffect(() => {
    const handleProfileImageUpdate = () => {
      const newImage = sessionStorage.getItem('image_url');
      setImageURL(newImage);
    };

    window.addEventListener('profileImageUpdated', handleProfileImageUpdate);
    return () => {
      window.removeEventListener('profileImageUpdated', handleProfileImageUpdate);
    };
  }, []);

  const handleNavigate = (e, path) => {
    e.preventDefault();
    window.location.href = path;
  };

  const handleClick = (e) => {
    e.preventDefault();
    setSettingsOpen((prev) => !prev);
    setWasManuallyToggled(true);
    window.location.href = '/settings/org-members';
  };

  const handleMouseEnter = () => {
    setHoveringSettings(true);
  };

  const handleMouseLeave = () => {
    setHoveringSettings(false);
  };

  const handleLogout = useCallback((e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    console.log("logout");
    window.location.href = logout_url;
  }, []);

  // Auto-logout after 15 minutes of inactivity
  const timerRef = useRef(null);
  const TIMEOUT_DURATION = 15 * 60 * 1000; // 15 minutes

  const resetTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      console.log('Session timed out due to inactivity.');
      handleLogout();
    }, TIMEOUT_DURATION);
  }, [handleLogout]);

  useEffect(() => {
    const events = ['mousemove', 'click', 'keypress', 'scroll', 'keydown'];

    // Reset timer on any user activity
    const handleActivity = () => {
      resetTimer();
    };

    // Initial start
    resetTimer();

    // Attach listeners
    events.forEach(event => {
      window.addEventListener(event, handleActivity);
    });

    // Cleanup
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [resetTimer]);

  const handleToggleSidebar = () => {
    onToggleSidebar();
  };

  return (
    <div className={`sidebar p-3 ${isCollapsed ? 'collapsed' : ''}`} id="sidebar1">
      <div className="logo-container text-center">
        <img src={Logo} alt="CSAT Logo" className="img-fluid mb-4" id="nav-1" />
      </div>
      <center>
        {imageURL ? (
          <img
            src={imageURL}
            alt="User Profile"
            className="img-fluid mx-auto d-block mb-4"
            id="nav-2"
          />
        ) : (
          <div>Image Failed</div>
        )}
      </center>
      <br />
      <center>
        <strong className="sidebar-text">{userName || "No Name"}</strong>
      </center>
      <br />
      <div>
        {moduleAccess.Surveys && moduleAccess.Surveys !== 'None' && (
          <>
            <a className={`nav-link ${location.pathname === '/survey-dashboard' ? 'active' : ''}`} href="/survey-dashboard" onClick={(e) => handleNavigate(e, '/survey-dashboard')} title={isCollapsed ? "Survey Dashboard" : ""}>
              <DashboardIcon className="logo-img" />
              <span className="sidebar-text">Survey Dashboard</span>
            </a>
            <a className={`nav-link ${location.pathname === '/surveys' ? 'active' : ''}`} href="/surveys" onClick={(e) => handleNavigate(e, '/surveys')} title={isCollapsed ? "Survey Templates" : ""}>
              <AssignmentIcon className="logo-img" />
              <span className="sidebar-text">Survey Templates</span>
            </a>
          </>
        )}

        {moduleAccess.Projects && moduleAccess.Projects !== 'None' && (
          <a className={`nav-link ${location.pathname === '/projects' ? 'active' : ''}`} href="/projects" onClick={(e) => handleNavigate(e, '/projects')} title={isCollapsed ? "Projects" : ""}>
            <BusinessCenterIcon className="logo-img" />
            <span className="sidebar-text">Projects</span>
          </a>
        )}

        {moduleAccess.Clients && moduleAccess.Clients !== 'None' && (
          <a className={`nav-link ${location.pathname === '/clients' ? 'active' : ''}`} href="/clients" onClick={(e) => handleNavigate(e, '/clients')} title={isCollapsed ? "Clients" : ""}>
            <BusinessIcon className="logo-img" />
            <span className="sidebar-text">Clients</span>
          </a>
        )}

        {/* {moduleAccess.Reports && moduleAccess.Reports !== 'None' && (
          <a className={`nav-link ${location.pathname === '/reports' ? 'active' : ''}`} href="/reports" onClick={(e) => handleNavigate(e, '/reports')} title={isCollapsed ? "Reports" : ""}>
            <BarChartIcon className="logo-img" />
            <span className="sidebar-text">Reports</span>
          </a>
        )} */}

        {moduleAccess.Settings && moduleAccess.Settings !== 'None' && (
          <div>
            <a
              href="/settings/org-members"
              className={`nav-link settings-link ${location.pathname.startsWith('/settings') || location.pathname === '/roles' ? 'active' : ''}`}
              onClick={handleClick}
              onMouseEnter={isCollapsed ? handleMouseEnter : undefined}
              onMouseLeave={isCollapsed ? handleMouseLeave : undefined}
              title={isCollapsed ? "Settings" : ""}
            >
              <SettingsIcon className="logo-img" />
              <span className="sidebar-text">Settings</span>

              {isCollapsed && hoveringSettings && (
                <div className="hover-popup" id="hover-popup1">
                  <a href="/settings/org-members" onClick={(e) => handleNavigate(e, '/settings/org-members')}>Org Members</a>
                  <a href="/settings/teams" onClick={(e) => handleNavigate(e, '/settings/teams')}>Teams</a>
                  <a href="/roles" onClick={(e) => handleNavigate(e, '/roles')}>Roles</a>
                </div>
              )}
            </a>

            {!isCollapsed && settingsOpen && (
              <div className="submenu-below">
                <a href="/settings/org-members" onClick={(e) => handleNavigate(e, '/settings/org-members')} title={isCollapsed ? "Org Members" : ""}>
                  <GroupIcon className="logo-img" />
                  <span className="sidebar-text">Org Members</span>
                </a>
                <a href="/roles" onClick={(e) => handleNavigate(e, '/roles')} title={isCollapsed ? "Roles" : ""}>
                  <AdminPanelSettingsIcon className="logo-img" />
                  <span className="sidebar-text">Roles</span>
                </a>
              </div>
            )}
          </div>
        )}
        {moduleAccess.Profile && moduleAccess.Profile !== 'None' && (
          <a className={`nav-link ${location.pathname === '/profile' ? 'active' : ''}`} href="/profile" onClick={(e) => handleNavigate(e, '/profile')} title={isCollapsed ? "Profile" : ""}>
            <AccountCircleIcon className="logo-img" />
            <span className="sidebar-text">Profile</span>
          </a>
        )}
        <button
          onClick={handleLogout}
          className="nav-link11"
          title={isCollapsed ? "Logout" : ""}
        >
          <LogoutIcon className="logo-img" />
          <span className="sidebar-text">Logout</span>
        </button>
      </div>

      <div className="toggle-sidebar-container">
        <button
          onClick={handleToggleSidebar}
          className="nav-link11 toggle-sidebar-btn"
          title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
        >
          {isCollapsed ? <LastPageIcon className="logo-img" /> : <FirstPageIcon className="logo-img" />}
        </button>
      </div>
    </div>
  );
};

Sidebar.propTypes = {
  onToggleSidebar: PropTypes.func,
  isCollapsed: PropTypes.bool,
};

Sidebar.defaultProps = {
  onToggleSidebar: () => { },
  isCollapsed: false,
};

export default Sidebar;
